import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Bot, X, Send, Loader2, ChevronDown, ChevronUp, 
  HelpCircle, Mail, FileText, CheckSquare, Bell,
  Play, Pause, RefreshCw, Settings, History, Zap,
  AlertCircle, WifiOff
} from 'lucide-react';
import * as api from '../services/api';
import { toast } from 'sonner';

export const GlobalAgentWidget = ({ currentUser, activeTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [globalAgent, setGlobalAgent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [pendingTasks, setPendingTasks] = useState([]); // Agent tasks (for approval)
  const [userTasks, setUserTasks] = useState([]); // User's CRM tasks
  const [executingTask, setExecutingTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [taskHistory, setTaskHistory] = useState([]);
  const chatEndRef = useRef(null);

  // Fetch global agent on mount
  useEffect(() => {
    fetchGlobalAgent();
    fetchUserTasks(); // Also fetch user's CRM tasks
  }, []);

  // Setup polling for tasks once globalAgent is loaded
  useEffect(() => {
    if (!globalAgent?.id) return;
    
    // Initial fetch
    fetchPendingTasks();
    
    // Poll every 10 seconds
    const interval = setInterval(() => {
      if (globalAgent?.id) {
        fetchPendingTasks();
        fetchUserTasks();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [globalAgent?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchGlobalAgent = async () => {
    try {
      setLoadError(null);
      const response = await api.default.get('/agents/global');
      const data = response.data || response;
      if (data) {
        setGlobalAgent(data);
        setMessages([{
          id: '1',
          sender: 'agent',
          text: `¡Hola! Soy ${data.name}, tu asistente de IA. Estoy disponible en todas las páginas del CRM para ayudarte. Puedo enviar emails, crear documentos, gestionar tareas y más. ¿En qué te puedo ayudar?`,
          timestamp: new Date()
        }]);
      } else {
        setLoadError('no_agent');
      }
    } catch (error) {
      console.log('Error loading global agent:', error);
      setLoadError(error.response?.status === 404 ? 'no_agent' : 'network');
    }
  };

  const retryFetch = async () => {
    setIsRetrying(true);
    await fetchGlobalAgent();
    setIsRetrying(false);
  };

  const fetchPendingTasks = async () => {
    if (!globalAgent?.id) return;
    try {
      const response = await api.default.get(`/agents/${globalAgent.id}/pending-tasks`);
      const data = response.data || response;
      setPendingTasks(data.tasks || []);
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
    }
  };

  const fetchUserTasks = async () => {
    try {
      const response = await api.default.get('/tasks');
      const data = response.data || response;
      // Filter for pending tasks of the CURRENT USER only
      const pending = (Array.isArray(data) ? data : []).filter(t => 
        ['Pendiente', 'pending', 'En Progreso'].includes(t.status) &&
        (t.assigned_to === currentUser?.name || !t.assigned_to) // User's tasks or unassigned
      );
      setUserTasks(pending);
    } catch (error) {
      console.error('Error fetching user tasks:', error);
    }
  };

  const fetchTaskHistory = async () => {
    if (!globalAgent?.id) return;
    try {
      const response = await api.default.get(`/agents/${globalAgent.id}/task-history`);
      const data = response.data || response;
      setTaskHistory(data.tasks || []);
    } catch (error) {
      console.error('Error fetching task history:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { 
      id: Date.now().toString(), 
      sender: 'user', 
      text: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Send to agent for processing
      const response = await api.default.post(`/agents/${globalAgent?.id || 'global'}/chat`, {
        message: input,
        context: {
          currentPage: activeTab,
          currentUrl: window.location.href,
          userId: currentUser?.id,
          userName: currentUser?.name
        }
      });
      
      const data = response.data || response;
      
      const agentMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'agent',
        text: data.response || data.message || 'Entendido. Procesando tu solicitud...',
        action: data.action,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentMessage]);
      
      // If action was executed, refresh pending tasks
      if (data.action_executed) {
        fetchPendingTasks();
        toast.success(`Acción ejecutada: ${data.action_type}`);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        id: Date.now().toString(), 
        sender: 'agent', 
        text: 'Lo siento, hubo un error al procesar tu solicitud. ¿Puedes intentarlo de nuevo?',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeTask = async (task) => {
    setExecutingTask(task.id);
    try {
      const response = await api.default.post(`/agents/${globalAgent.id}/execute-task/${task.id}`);
      const data = response.data || response;
      
      if (data.success) {
        toast.success(`Tarea ejecutada: ${task.title}`);
        fetchPendingTasks();
        
        // Add message about execution
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          sender: 'agent',
          text: `✅ Ejecuté la tarea: "${task.title}"\n\nResultado: ${data.result || 'Completado exitosamente'}`,
          timestamp: new Date()
        }]);
      } else {
        toast.error(`Error: ${data.error}`);
      }
    } catch (error) {
      toast.error('Error al ejecutar la tarea');
    } finally {
      setExecutingTask(null);
    }
  };

  const pageContextMap = {
    'dashboard': 'Panel de Control - estadísticas y resumen',
    'empresas': 'Gestión de Empresas - clientes y prospectos',
    'tareas': 'Gestión de Tareas - pendientes y seguimientos',
    'embudos': 'Embudos de Venta - oportunidades y deals',
    'evaluaciones': 'Evaluaciones Comerciales',
    'formularios': 'Formularios Públicos',
    'marketing': 'Marketing y Social Media',
    'agents': 'Configuración de Agentes de IA',
    'gmail': 'Panel de Gmail',
    'google-tools': 'Herramientas de Google',
    'reportes': 'Reportes y Análisis',
    'admin': 'Administración del Sistema'
  };

  // Show error state with retry option
  if (loadError) {
    return createPortal(
      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 999999 }}>
        <div className="relative">
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="bg-gradient-to-r from-gray-500 to-gray-600 text-white border-none rounded-full w-14 h-14 flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-all hover:scale-105"
            title="Agente no disponible"
          >
            {isOpen ? <X size={24} /> : (loadError === 'network' ? <WifiOff size={24} /> : <Bot size={24} className="opacity-50" />)}
          </button>
        </div>
        
        {isOpen && (
          <div className="fixed bottom-20 right-5 w-80 bg-white rounded-xl shadow-2xl p-4 border border-gray-200">
            <div className="text-center space-y-3">
              {loadError === 'network' ? (
                <>
                  <WifiOff className="mx-auto text-red-500" size={40} />
                  <h3 className="font-semibold text-gray-900">Error de Conexión</h3>
                  <p className="text-sm text-gray-500">No se pudo conectar con el servidor. Verifica tu conexión a internet.</p>
                </>
              ) : (
                <>
                  <AlertCircle className="mx-auto text-amber-500" size={40} />
                  <h3 className="font-semibold text-gray-900">Sin Agente Global</h3>
                  <p className="text-sm text-gray-500">No hay un agente global configurado. Ve a Agentes de IA para crear uno.</p>
                </>
              )}
              <button
                onClick={retryFetch}
                disabled={isRetrying}
                className="w-full px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isRetrying ? (
                  <><Loader2 size={16} className="animate-spin" /> Reintentando...</>
                ) : (
                  <><RefreshCw size={16} /> Reintentar</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>,
      document.body
    );
  }

  if (!globalAgent) return null;

  return createPortal(
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 999999 }} data-testid="global-agent-widget-container">
      {/* Main Toggle Button */}
      <div className="relative">
        {userTasks.length > 0 && (
          <span 
            className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center animate-pulse"
            data-testid="global-agent-pending-badge"
            title={`${userTasks.length} tareas pendientes`}
          >
            {userTasks.length > 99 ? '99+' : userTasks.length}
          </span>
        )}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-none rounded-full w-14 h-14 flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-all hover:scale-105"
          title={globalAgent.name}
          data-testid="global-agent-widget-button"
        >
          {isOpen ? <X size={24} /> : <Bot size={24} />}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-5 w-96 h-[550px] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-amber-400" />
              <div>
                <div className="font-semibold text-sm">{globalAgent.name}</div>
                <div className="text-xs text-gray-400">Agente Global • {pageContextMap[activeTab] || activeTab}</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button 
                onClick={() => setShowTutorial(true)}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Tutorial"
              >
                <HelpCircle size={16} />
              </button>
              <button 
                onClick={() => { setShowHistory(!showHistory); fetchTaskHistory(); }}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Historial"
              >
                <History size={16} />
              </button>
              <button 
                onClick={fetchPendingTasks}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                title="Actualizar"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {/* Pending Tasks Banner - Shows agent tasks for approval */}
          {pendingTasks.length > 0 && (
            <div className="bg-amber-50 border-b border-amber-200 p-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-amber-800 flex items-center gap-1">
                  <Zap size={14} />
                  {pendingTasks.length} acción(es) pendiente(s) de aprobación
                </span>
              </div>
              <div className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                {pendingTasks.slice(0, 3).map(task => (
                  <div key={task.id} className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs">
                    <span className="truncate flex-1">{task.title}</span>
                    <button
                      onClick={() => executeTask(task)}
                      disabled={executingTask === task.id}
                      className="ml-2 px-2 py-0.5 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                    >
                      {executingTask === task.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* User Tasks Summary Banner */}
          {userTasks.length > 0 && (
            <div className="bg-blue-50 border-b border-blue-200 p-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-800 flex items-center gap-1">
                  <CheckSquare size={14} />
                  Tienes {userTasks.length} tarea(s) pendiente(s) en el CRM
                </span>
                <button
                  onClick={() => setInput('¿Cuáles son mis tareas pendientes?')}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Ver detalles
                </button>
              </div>
            </div>
          )}

          {/* Messages or History */}
          <div className="flex-1 p-3 overflow-y-auto bg-gray-50 text-sm">
            {showHistory ? (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700 mb-2">Historial de Tareas</h4>
                {taskHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay historial aún</p>
                ) : (
                  taskHistory.map(task => (
                    <div key={task.id} className="bg-white rounded-lg p-2 border text-xs">
                      <div className="flex justify-between items-start">
                        <span className="font-medium">{task.title}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          task.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          task.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="text-gray-500 mt-1">{new Date(task.executed_at || task.created_at).toLocaleString()}</div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <>
                {messages.map(msg => (
                  <div key={msg.id} className={`mb-3 ${msg.sender === 'user' ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block max-w-[85%] px-3 py-2 rounded-lg ${
                      msg.sender === 'user' 
                        ? 'bg-amber-500 text-white rounded-br-sm' 
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                    }`}>
                      <div className="whitespace-pre-wrap">{msg.text}</div>
                      <div className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-amber-100' : 'text-gray-400'}`}>
                        {msg.timestamp?.toLocaleTimeString?.() || ''}
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="text-left mb-3">
                    <div className="inline-block bg-white border border-gray-200 px-3 py-2 rounded-lg">
                      <Loader2 size={16} className="animate-spin text-amber-500" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
          </div>

          {/* Quick Actions */}
          <div className="px-3 py-2 border-t border-gray-100 bg-white">
            <div className="flex gap-1 overflow-x-auto pb-2">
              {[
                { icon: Mail, label: 'Email', action: 'enviar email' },
                { icon: FileText, label: 'Doc', action: 'crear documento' },
                { icon: CheckSquare, label: 'Tarea', action: 'crear tarea' },
                { icon: Bell, label: 'Nota', action: 'agregar nota' },
              ].map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(`Quiero ${item.action}`)}
                  className="flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-600 whitespace-nowrap"
                >
                  <item.icon size={12} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Escribe una instrucción..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !input.trim()}
                className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tutorial Modal */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000000]" onClick={() => setShowTutorial(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-t-xl">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Bot size={20} />
                Tutorial de Agentes de IA
              </h3>
              <button onClick={() => setShowTutorial(false)} className="p-1 hover:bg-white/20 rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <section>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">1</span>
                  ¿Qué es un Agente Global?
                </h4>
                <p className="text-sm text-gray-600">
                  Un agente global está disponible en <strong>todas las páginas del CRM</strong>. 
                  Puede leer el contexto de la página actual y ayudarte con tareas específicas según donde estés.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">2</span>
                  Capacidades del Agente
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2"><Mail size={14} className="text-amber-500" /> <strong>Enviar emails</strong> usando templates o personalizados</li>
                  <li className="flex items-center gap-2"><FileText size={14} className="text-amber-500" /> <strong>Crear documentos</strong> y propuestas</li>
                  <li className="flex items-center gap-2"><CheckSquare size={14} className="text-amber-500" /> <strong>Gestionar tareas</strong> - crear, completar, seguimientos</li>
                  <li className="flex items-center gap-2"><Bell size={14} className="text-amber-500" /> <strong>Agregar notas</strong> a empresas y contactos</li>
                  <li className="flex items-center gap-2"><Zap size={14} className="text-amber-500" /> <strong>Ejecutar automatizaciones</strong> de n8n</li>
                </ul>
              </section>

              <section>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">3</span>
                  Recibir Tareas de n8n
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  El agente puede recibir instrucciones automáticas desde n8n. Configura un webhook HTTP en n8n apuntando a:
                </p>
                <code className="text-xs bg-gray-100 p-2 rounded block overflow-x-auto">
                  POST /api/agents/{'{agent_id}'}/receive-task
                </code>
                <p className="text-xs text-gray-500 mt-2">
                  Las tareas aparecerán en el banner amarillo y podrás ejecutarlas manualmente o programar ejecución automática.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">4</span>
                  Ejemplos de Comandos
                </h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <div className="bg-gray-50 p-2 rounded">
                    <strong>&quot;Envía un email de seguimiento a Empresa ABC&quot;</strong>
                    <p className="text-xs text-gray-500">Usa el template de seguimiento para enviar email</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <strong>&quot;Crea una tarea de llamada para mañana&quot;</strong>
                    <p className="text-xs text-gray-500">Crea una tarea con fecha de mañana</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <strong>&quot;Marca como completada la tarea X&quot;</strong>
                    <p className="text-xs text-gray-500">Actualiza el estado de la tarea</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <strong>&quot;Agrega una nota: Cliente interesado en servicio premium&quot;</strong>
                    <p className="text-xs text-gray-500">Añade nota a la empresa actual</p>
                  </div>
                </div>
              </section>

              <section>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm">5</span>
                  Aprendizaje Automático
                </h4>
                <p className="text-sm text-gray-600">
                  El agente aprende de tus aprobaciones y rechazos. Cuando apruebas una acción, 
                  el agente guarda ese patrón como &quot;exitoso&quot;. Los rechazos le enseñan qué evitar.
                </p>
              </section>
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button 
                onClick={() => setShowTutorial(false)}
                className="w-full py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium"
              >
                ¡Entendido!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default GlobalAgentWidget;
